import { useEffect, useRef, useState } from "react";

// The sticky header (top bar + chip strip) that a jumped-to section must clear.
export const JUMP_OFFSET = "scroll-mt-[118px]";
// A section whose top has passed this line is the one the guest is reading.
// Slightly below the sticky header, so a just-jumped-to section counts as its own.
const SPY_LINE = 130;

/**
 * Chips over a long sectioned scroll: the active chip follows the guest down
 * the page, and a tap jumps to the section. Shared by the full-menu and drinks
 * screens — sections carry ids `${sectionPrefix}${id}`, chips `${chipPrefix}${id}`.
 */
export const useScrollSpy = (sectionIds, sectionPrefix, chipPrefix) => {
  // A tap sets the active chip directly and silences the spy for the smooth
  // scroll, so the strip does not flicker through every section on the way down.
  const [activeId, setActiveId] = useState(null);
  const suppressUntilRef = useRef(0);

  // The spy: the last section whose header has crossed the line is the active
  // one. A plain scroll listener rather than an IntersectionObserver, because
  // the question is "which section am I in", not "what became visible".
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        if (Date.now() < suppressUntilRef.current) return;
        let current = sectionIds[0] || null;
        for (const id of sectionIds) {
          const el = document.getElementById(`${sectionPrefix}${id}`);
          if (el && el.getBoundingClientRect().top <= SPY_LINE) current = id;
          else break;
        }
        setActiveId(current);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [sectionIds, sectionPrefix]);

  // Keep the active chip in view: the strip follows the guest down the page.
  // block: "nearest" so centring the chip never scrolls the page itself.
  useEffect(() => {
    if (!activeId) return;
    document
      .getElementById(`${chipPrefix}${activeId}`)
      ?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [activeId, chipPrefix]);

  const jumpTo = (id) => {
    suppressUntilRef.current = Date.now() + 900;
    setActiveId(id);
    document
      .getElementById(`${sectionPrefix}${id}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const chipClass = (id) =>
    `px-4 h-10 rounded-full text-sm font-bold whitespace-nowrap transition-colors flex items-center gap-1.5 ${
      activeId === id
        ? "bg-neutral-900 text-white"
        : "bg-white text-neutral-700 border border-neutral-200"
    }`;

  return { activeId, jumpTo, chipClass };
};
